import type { IUser } from '../models/auth';

export type NavChild = {
  to: string;
  label: string;
  iconClass?: string;
  permissions?: string[];
};

export type NavGroup = {
  type: 'group';
  label: string;
  iconClass?: string;
  sectionPermissions: string[];
  children: NavChild[];
};

export type NavStandalone = {
  type: 'link';
  to: string;
  label: string;
  iconClass?: string;
  permissions: string[];
};

export type NavMenuEntry = NavGroup | NavStandalone;

export const NAV_MENU: NavMenuEntry[] = [
  {
    type: 'group',
    label: 'Search',
    iconClass: 'fa-search',
    sectionPermissions: ['directbill_account_manage', 'person_search'],
    children: [
      { to: '/search', label: 'Search Customers', iconClass: 'fa-search' },
      { to: '/online-user/search', label: 'Online Users', iconClass: 'fa-globe' },
      { to: '/person-search', label: 'Numbers for Name', iconClass: 'fa-address-book', permissions: ['person_search'] },
    ],
  },
  {
    type: 'group',
    label: 'Phone Cards',
    iconClass: 'fa-list-alt',
    sectionPermissions: ['phonecard_manage'],
    children: [
      { to: '/card/inventory', label: 'Inventory' },
      { to: '/card/transfer', label: 'Transfer' },
      { to: '/card/change-value', label: 'Change Value' },
    ],
  },
  {
    type: 'group',
    label: 'Facilities',
    iconClass: 'fa-building',
    sectionPermissions: ['facility_user_manage', 'facility_manage'],
    children: [
      { to: '/facilities', label: 'List', permissions: ['facility_manage'] },
      { to: '/facility-users', label: 'Users', permissions: ['facility_user_manage'] },
      {
        to: '/api-log/facility-users',
        label: 'Facility Users Log',
        permissions: ['api_log_search', 'facility_user_log_search'],
      },
    ],
  },
  {
    type: 'group',
    label: 'Inmates',
    iconClass: 'fa-users',
    sectionPermissions: ['inmate_manage'],
    children: [
      { to: '/inmates', label: 'List' },
      {
        to: '/inmate-account-payment',
        label: 'Account Payments',
        permissions: ['inmate_account_payment_search'],
      },
      {
        to: '/av-inmate-account-payment',
        label: 'AV Wallet Payments',
        permissions: ['inmate_account_payment_search'],
      },
      {
        to: '/inmate-account/commissary-transfer',
        label: 'Texting/Video Purchases',
        permissions: ['commissary_transfer_manage'],
      },
      {
        to: '/inmates/blacklist',
        label: 'Device Blacklist',
        permissions: ['inmate_device_blacklist_manage'],
      },
    ],
  },
  {
    type: 'group',
    label: 'Mobile Devices',
    iconClass: 'fa-mobile',
    sectionPermissions: ['mobile_device_manage'],
    children: [
      { to: '/mobile-device', label: 'List' },
      { to: '/mobile-device/unlocked', label: 'Unlocked' },
      { to: '/profiles', label: 'Profiles', permissions: ['mobile_device_profile_manage'] },
      { to: '/mobile-device/status', label: 'Status' },
      { to: '/mobile-device/monitoring', label: 'Monitoring' },
      { to: '/mobile-device/app-management', label: 'App Management', permissions: ['mobile_device_app_manage'] },
      {
        to: '/mobile-device-purchase',
        label: 'Purchases',
        permissions: ['mobile_device_purchase_manage'],
      },
      {
        to: '/shipping-ticket/outbound',
        label: 'Outbound Tickets',
        permissions: ['shipping_ticket_manage'],
      },
      {
        to: '/shipping-ticket/inbound',
        label: 'Inbound Tickets',
        permissions: ['shipping_ticket_manage'],
      },
      { to: '/mobile-device/needs-inspection', label: 'Needing Inspection' },
      { to: '/repair-ticket', label: 'Repair Tickets', permissions: ['repair_ticket_manage'] },
    ],
  },
  {
    type: 'group',
    label: 'Reference Material',
    iconClass: 'fa-photo-video',
    sectionPermissions: ['reference_material_manage'],
    children: [
      { to: '/reference-material', label: 'List' },
      { to: '/reference-material/category', label: 'Categories' },
    ],
  },
  {
    type: 'link',
    to: '/acknowledgements',
    label: 'Acknowledgements',
    iconClass: 'fa-file-circle-check',
    permissions: ['facility_acknowledgement_manage'],
  },
  {
    type: 'group',
    label: 'Inmate Requests',
    iconClass: 'fa-inbox',
    sectionPermissions: ['inmate_request_form_manage'],
    children: [
      { to: '/inmate-requests/groups', label: 'Groups' },
      { to: '/inmate-requests/forms', label: 'Forms' },
    ],
  },
  {
    type: 'group',
    label: 'Entertainment',
    iconClass: 'fa-tv',
    sectionPermissions: ['music_manage', 'game_manage', 'tv_source_manage'],
    children: [
      { to: '/music', label: 'Music', permissions: ['music_manage'] },
      { to: '/games', label: 'Games', permissions: ['game_manage'] },
      { to: '/tv/streams', label: 'TV Streams', permissions: ['tv_source_manage'] },
    ],
  },
  {
    type: 'group',
    label: 'Rate Plans',
    iconClass: 'fa-calculator',
    sectionPermissions: ['rate_manage', 'service_rate_manage'],
    children: [
      { to: '/rate-plans', label: 'Telephony', permissions: ['rate_manage'] },
      { to: '/service-rate', label: 'Services', permissions: ['service_rate_manage'] },
    ],
  },
  {
    type: 'group',
    label: 'Credit Cards',
    iconClass: 'fa-credit-card',
    sectionPermissions: ['credit_card_search'],
    children: [
      { to: '/credit-card/charges', label: 'Charges' },
      { to: '/credit-card', label: 'Card Search' },
    ],
  },
  {
    type: 'group',
    label: 'SMS/Voice Numbers',
    iconClass: 'fa-tty',
    sectionPermissions: ['sms_voice_number_manage', 'sms_voice_number_search'],
    children: [
      { to: '/sms-voice-numbers', label: 'Inventory' },
      { to: '/sms-voice-numbers/issues', label: 'Issues' },
      { to: '/sms-voice-numbers/usage-summary', label: 'Usage Summary' },
    ],
  },
  {
    type: 'group',
    label: 'Support',
    iconClass: 'fa-life-ring',
    sectionPermissions: ['text_message_staff_send'],
    children: [
      { to: '/support/inmate-texting', label: 'Inmate Texting' },
      { to: '/support/phone', label: 'Phone' },
    ],
  },
  {
    type: 'group',
    label: 'Users',
    iconClass: 'fa-clipboard',
    sectionPermissions: ['api_credential_manage', 'staff_user_manage'],
    children: [
      {
        to: '/staff-user',
        label: 'Staff Users',
        iconClass: 'fa-users',
        permissions: ['api_credential_manage', 'staff_user_manage'],
      },
      {
        to: '/api-log/staff-users',
        label: 'Staff Users Log',
        iconClass: 'fa-cloud',
        permissions: ['api_log_search', 'staff_user_log_search'],
      },
      {
        to: '/api-role',
        label: 'API Roles',
        iconClass: 'fa-user-plus',
        permissions: ['api_credential_manage'],
      },
    ],
  },
  {
    type: 'group',
    label: 'Records',
    iconClass: 'fa-mail-bulk',
    sectionPermissions: ['call_search', 'text_message_search', 'text_message_status_search'],
    children: [
      { to: '/cdr', label: 'CDRs', iconClass: 'fa-phone', permissions: ['call_search'] },
      {
        to: '/text-message',
        label: 'Text Messages',
        iconClass: 'fa-comments',
        permissions: ['text_message_search'],
      },
      {
        to: '/text-messages-status',
        label: 'Texting Provider Status',
        iconClass: 'fa-file-alt',
        permissions: ['text_message_status_search'],
      },
    ],
  },
  {
    type: 'group',
    label: 'Mail Connect',
    iconClass: 'fa-envelope',
    sectionPermissions: ['postal_mail_manage'],
    children: [
      { to: '/mail-connect/scan', label: 'Scanning' },
      { to: '/mail-connect', label: 'Inmate Mail' },
    ],
  },
  {
    type: 'link',
    to: '/call-route',
    label: 'Call Routes',
    iconClass: 'fa-code-branch',
    permissions: ['call_route_manage'],
  },
  {
    type: 'link',
    to: '/invoice',
    label: 'Invoicing',
    iconClass: 'fa-file-invoice-dollar',
    permissions: ['invoice_manage'],
  },
  {
    type: 'group',
    label: 'Tax Rates/Tables',
    iconClass: 'fa-money-check-alt',
    sectionPermissions: ['tax_manage'],
    children: [
      { to: '/tax/rates', label: 'Tax Rates', iconClass: 'fa-money-check-alt' },
      { to: '/tax/tables', label: 'Tax Tables', iconClass: 'fa-money-check-alt' },
    ],
  },
  {
    type: 'link',
    to: '/translations',
    label: 'Translations',
    iconClass: 'fa-language',
    permissions: ['translation_manage'],
  },
  {
    type: 'link',
    to: '/changelog',
    label: 'Changelog',
    iconClass: 'fa-clipboard-list',
    permissions: ['changelog_view'],
  },
];

export function filterNavMenuForUser(user: IUser): NavMenuEntry[] {
  const out: NavMenuEntry[] = [];
  for (const entry of NAV_MENU) {
    if (entry.type === 'link') {
      if (user.hasOneOfPermissions(entry.permissions)) {
        out.push(entry);
      }
      continue;
    }
    if (!user.hasOneOfPermissions(entry.sectionPermissions)) {
      continue;
    }
    const children = entry.children.filter(
      (c) => !c.permissions?.length || user.hasOneOfPermissions(c.permissions)
    );
    if (children.length === 0) {
      continue;
    }
    out.push({ ...entry, children });
  }
  return out;
}
